import React, { useEffect, useState, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ButtonField from 'src/Components/Ui/ButtonField';
import { UpdateUserEmail, auth } from '../../../Services/FirebaseService';
import Teachr from '../../../Objects/models/Teachr';
import { Subject } from '../../../Objects/models/Subject';
import useTailwindBrand from '../../../Theme/useTailwindBrand';
import { FormErrors, FormState, useFormState } from '../../../Hooks/Auth/useFormState';
import { ENV_DECLARONS, ENV_TEACHR, ENV_VERIFIED_TRAINER, getActiveAppBrandConfig, getActiveClientBrandConfig, getActivePrestataireBrandConfig, getActivePrestationBrandConfig, isBrandActive } from '../../../config/environment.config';
import { useBrandAuthConfig } from '../../../Hooks/Auth/useBrandAuthConfig';
import validateEmail from '../../../Validation/Email';
import { validatePhoneNumber } from '../../../Validation/Phone';
import SponsorshipRepository from '../../../Repositories/SponsorshipRepository';
import { SHOW_ERROR } from '../../../Redux/Reducers/MessageReducers';
import ApiService from '../../../Services/ApiService';
import { userTeachrSetAccountAction } from '../../../Redux/Actions/UserActions';
import Sponsorship from '../../../Objects/models/Sponsorship';
import TeachrRepository from '../../../Repositories/TeachrRepository';
import Functions from '../../../Helpers/Functions';
// import LayoutSteps from '../LayoutSteps';
import FormInput from '../../../Components/Ui/FormInput';
import useFormValidation from '../../../Hooks/Auth/useFormValidation';
import FormSubjectSelect from '../../../Components/FormFields/FormSubjectSelect';
import LayoutSteps from '../LayoutSteps';
import IdentityFields from './IdentityFields';
import ReferralFields from './ReferralFields';

interface RegisterSecondProps {
  nextStep: () => void;
  prevStep: () => void;
}

function RegisterSecond({ nextStep }: RegisterSecondProps) {
  const dispatch = useDispatch();
  const [user] = useAuthState(auth);
  const teachr: Teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
  const [courses, setCourses] = useState<Subject[]>(teachr?.subjectToTeach ?? []);
  const [option, setOption] = useState<number | any>(teachr?.recommandedBy ? 1 : teachr?.sponsorship ? 2 : null);
  const [homeCoursesOnly, setHomeCoursesOnly] = useState<boolean | null>(null);
  const [show, setShow] = useState(false);
  // Collecte du nombre d'élèves : uniquement pour les marques teachr et declarons
  const showStudentCount = isBrandActive(ENV_TEACHR) || isBrandActive(ENV_DECLARONS);
  const [studentCount, setStudentCount] = useState<number | ''>(teachr?.numberOfStudents ?? '');
  const [wantsAdvice, setWantsAdvice] = useState<boolean | null>(null);
  const { classes } = useTailwindBrand();
  const { validateField, validateRegisterFirstForm } = useFormValidation();
  // téléphone demandé quand aucun n'est connu : connexion Google, ou reprise
  // d'une inscription abandonnée (le brouillon Redux de l'étape 1 est perdu)
  const showPhoneInput = !user?.phoneNumber
    && (user?.providerData[0]?.providerId === 'google.com' || !teachr?.phone);

  const { form, errors, setField, clearError, setErrors } = useFormState({
    firstName: teachr?.firstName ?? '',
    lastName: teachr?.lastName ?? '',
    birthday: teachr?.birthday ?? '',
    gender: teachr?.sex ?? '',
    email: teachr?.mail ?? '',
    phone: teachr?.phone ?? '',
    sponsorship: teachr?.sponsorship ?? '',
    recommandedBy: teachr?.recommandedBy ?? '',
    option: option,
  });

  useEffect(() => {
    if (teachr) {
      if (teachr.birthday) {
        setField('birthday', moment(teachr.birthday));
      }
      if (teachr.firstName) {
        setField('firstName', teachr.firstName);
      }
      if (teachr.lastName) {
        setField('lastName', teachr.lastName);
      }
    }
  }, [teachr, setField]);

  const getOption = useCallback(
    (value: number) => {
      setOption(value);
      setField('option', value);
    },
    [setField]
  );

  const handleGenderChange = (genderValue: 'MAN' | 'WOMAN') => {
    setField('gender', genderValue);
    // Validation en temps réel
    const updatedForm = { ...form, gender: genderValue };
    const error = validateField('gender', genderValue, updatedForm);
    if (error) {
      setErrors({ ...errors, gender: error });
    } else {
      clearError('gender');
    }
  };

  const handleBirthdayChange = (date: any) => {
    setField('birthday', date);
    // Validation en temps réel
    const updatedForm = { ...form, birthday: date };
    const error = validateField('birthday', date, updatedForm);
    if (error) {
      setErrors({ ...errors, birthday: error });
    } else {
      clearError('birthday');
    }
  };

  const subjectFormat = useCallback(
    (subjects: Subject[]) => subjects.map((subject) => subject.fr_FR).join(', '),
    []
  );

  const validate = useCallback(async () => {
    const validationErrors: Record<string, string> = {
      email: '',
      phone: '',
      firstName: '',
      lastName: '',
      gender: '',
      birthday: '',
      courses: '',
      sponsorship: '',
      numberOfStudents: '',
      wantsAdvice: '',
    };
    let isValid = true;

    // Validation email / téléphone selon le provider
    if (
      user?.providerData[0].providerId === 'phone' &&
      !validateEmail(form.email!).isValid
    ) {
      validationErrors.email = validateEmail(form.email!).message;
      isValid = false;
    }

    if (showPhoneInput && validatePhoneNumber(form.phone!)) {
      validationErrors.phone = validatePhoneNumber(form.phone!);
      isValid = false;
    }

    // Validation avec le hook
    const { errors: formErrors, isValid: formValid } =
      await validateRegisterFirstForm(
        {
          ...form,
          courses,
          sponsorship: form.sponsorship || '',
        },
        user?.providerData[0].providerId
      );

    Object.assign(validationErrors, formErrors);

    if (!formValid) {
      isValid = false;
    }

    // Vérification du code de parrainage (asynchrone)
    if (form.sponsorship) {
      try {
        setShow(true);
        const res = await SponsorshipRepository.retriveSponsorshipByCode(
          form.sponsorship
        );

        if (res) {
          validationErrors.sponsorship = '';
        } else {
          validationErrors.sponsorship = 'Code de parrainage invalide';
          isValid = false;
        }
      } catch (err: any) {
        dispatch({
          type: SHOW_ERROR,
          payload: err?.data?.error ?? 'Code de parrainage invalide',
        });
        validationErrors.sponsorship =
          err?.data?.error ?? 'Code de parrainage invalide';
        isValid = false;
      } finally {
        setShow(false);
      }
    }

    if (!isBrandActive(ENV_VERIFIED_TRAINER) && homeCoursesOnly === null) {
      validationErrors.homeCoursesOnly = 'Veuillez sélectionner une option';
      isValid = false;
    }

    // Nombre d'élèves (teachr / declarons) + question conditionnelle
    if (showStudentCount) {
      if (studentCount === '' || Number.isNaN(Number(studentCount)) || Number(studentCount) < 0) {
        validationErrors.numberOfStudents = `Veuillez indiquer votre nombre d'${getActiveClientBrandConfig('s')}`;
        isValid = false;
      } else if (Number(studentCount) < 3 && wantsAdvice === null) {
        validationErrors.wantsAdvice = 'Veuillez répondre à cette question';
        isValid = false;
      }
    }

    setErrors(validationErrors);
    return isValid;
  }, [user?.providerData, form, showPhoneInput, validateRegisterFirstForm, courses, homeCoursesOnly, showStudentCount, setErrors, dispatch, studentCount, wantsAdvice]);

  const setDescription = (): string => {
    return (`
      Bonjour,\n
      Je m'appelle ${form.firstName}. \n
      Je suis ${getActivePrestataireBrandConfig()} particulier ${!isBrandActive(ENV_VERIFIED_TRAINER) && `en ${subjectFormat(courses)}`}.\n
      J'accompagne les ${getActiveClientBrandConfig('s')} avec pédagogie et bienveillance et suis inscrit sur ${getActiveAppBrandConfig()} depuis ${moment().format('MMMM YYYY')}.\n
      Au plaisir d'échanger avec vous !\n
      Cordialement
    `);
  };

  // Après création du compte : si le prof a peu d'élèves et souhaite des conseils,
  // on mémorise ce souhait pour proposer un bouton vers la page partenaire à la
  // dernière étape (RegisterLast), plutôt que de rediriger automatiquement.
  const proceedAfterRegister = () => {
    if (
      showStudentCount &&
      studentCount !== '' &&
      Number(studentCount) < 3 &&
      wantsAdvice === true
    ) {
      sessionStorage.setItem('showPartnerAdvice', 'true');
    } else {
      sessionStorage.removeItem('showPartnerAdvice');
    }
    nextStep();
  };

  const handleTeachrRegister = async (
    teachrResponse: Teachr,
    token: string
  ) => {
    if (teachrResponse) {
      const serializedTeachr = JSON.parse(JSON.stringify(teachrResponse));
      ApiService.enableAuth(undefined, undefined, 'teachr ' + token);
      dispatch(userTeachrSetAccountAction(serializedTeachr));
      dispatch({
        type: 'SET_TEACHR_INFO',
        payload: {
          teachr: serializedTeachr,
          authorization: 'teachr ' + token,
        },
      });

      if (serializedTeachr && serializedTeachr.sponsorship) {
        try {
          const sponsorship = new Sponsorship(serializedTeachr?.sponsorship);
          const sponsor = await SponsorshipRepository.createSponsorship(
            sponsorship,
            serializedTeachr
          );
          if (sponsor) {
            proceedAfterRegister();
          }
        } catch (error) {
          console.error(error);
          proceedAfterRegister();
        }
      } else {
        proceedAfterRegister();
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const isValid = await validate();
    if (!isValid) return;
    setShow(true);
    if (user) {
      let jwtToken = await user.getIdToken();
      const teachrUpdated = new Teachr(
        jwtToken,
        form.firstName?.toLowerCase()!,
        form.lastName?.toLowerCase()!,
        // reprise d'inscription : le brouillon Redux peut manquer, l'e-mail
        // du compte Firebase fait alors référence
        teachr?.mail?.toLowerCase() || form.email?.toLowerCase() || user?.email?.toLowerCase() || '',
        Functions.formatPhoneNumber(form.phone || user?.phoneNumber || teachr?.phone || ''),
        'firebaseMessagingToken',
        form.gender!,
        teachr?.whereStudies ?? '',
        teachr?.yearInStudies ?? 0,
        setDescription(),
        form.birthday ? form.birthday.format('YYYY/MM/DD') : ''
      );
      teachrUpdated.subjectToTeach = isBrandActive(ENV_VERIFIED_TRAINER)
        ? ['/subjects/108']
        : teachr?.subjectToTeach && teachr.subjectToTeach === courses
          ? teachr.subjectToTeach
          : courses.map((subject) => subject['@id']);
      teachrUpdated.recommandedBy = form.recommandedBy?.toLowerCase() || Functions.recommandedByDatas[option - 1];
      teachrUpdated.sponsorship = form.sponsorship ? form.sponsorship.toUpperCase() : null;
      teachrUpdated.isOldFacturation = isBrandActive(ENV_VERIFIED_TRAINER) || homeCoursesOnly === false ? true : false;
      teachrUpdated.scoop = isBrandActive(ENV_VERIFIED_TRAINER) || homeCoursesOnly === false ? '10' : null;
      teachrUpdated.procuration = isBrandActive(ENV_VERIFIED_TRAINER) || homeCoursesOnly === false ? "10" : undefined;

      if (isBrandActive(ENV_DECLARONS)) {
        teachrUpdated.brand = ENV_DECLARONS;
      }

      if (showStudentCount && studentCount !== '') {
        teachrUpdated.numberOfStudents = Number(studentCount);
      }

      try {
        const responseTeachr = await TeachrRepository.createTeachr(teachrUpdated);
        handleTeachrRegister(responseTeachr, jwtToken);
      } catch (error: any) {
        if (error?._description?.includes('This user is already registered.')) {
          const existingTeachr = await TeachrRepository.getCurrentTeachr();
          handleTeachrRegister(existingTeachr, jwtToken);
        }
      } finally {
        setShow(false);
      }
    }
  };

  const handleRegister = async (event: React.FormEvent) => {
    event.preventDefault();
    const isValid = await validate();
    if (!isValid) return;

    try {
      if (user?.providerData[0].providerId === 'phone') {
        if (teachr) {
          teachr.phone = Functions.formatPhoneNumber(user.phoneNumber || '');
        }
        await UpdateUserEmail(form.email!);
      }
      await handleSubmit(event);
      // nextStep sera appelé dans handleTeachrRegister après succès
    } catch (error: any) {
      if (error?.message?.includes('mail')) {
        setErrors({ email: 'Ce mail est déjà utilisé' });
      }
    }
  };

  // const loading = externalLoading || show;
  const { designConfig } = useBrandAuthConfig();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setField(name as keyof FormState, value);

      // Validation en temps réel
      const error = validateField(name as keyof FormState, value, form);
      if (error) {
        setErrors({ [name]: error });
      } else {
        clearError(name as keyof FormErrors);
      }
    },
    [form, setField, setErrors, clearError, validateField]
  );

  return (
    <LayoutSteps
      designConfig={designConfig}
      loading={show}
      handleSubmit={handleRegister}
      // showPrevButton={true}
      // onPrevClick={prevStep}
      btnText="Valider"
      stepNumber={2}
      children={
        <div className="grid lg:grid-cols-2 grid-cols-1 gap-y-6 gap-x-4 relative w-full pt-6" key={2}>
          <IdentityFields
            values={{
              firstName: form.firstName || '',
              lastName: form.lastName || '',
              gender: form.gender || '',
              phone: form.phone || '',
              email: form.email || '',
            }}
            errors={errors as Record<string, string | undefined>}
            onFieldChange={(name, value) => handleChange({ target: { name, value } } as React.ChangeEvent<HTMLInputElement>)}
            onGenderChange={handleGenderChange}
            onBirthdayChange={handleBirthdayChange}
            birthdayDefault={teachr?.birthday}
            showPhone={showPhoneInput}
            showEmail={!!user?.phoneNumber}
          />

          <div
            className={`w-full ${isBrandActive(ENV_VERIFIED_TRAINER) ? 'hidden' : ''
              }`}
          >
            <FormSubjectSelect
              label={isBrandActive(ENV_DECLARONS) ? 'Quelle(s) prestation(s) souhaitez-vous donner ?' : 'Quelle(s) matière(s) souhaitez-vous enseigner ?'}
              placeholder={isBrandActive(ENV_DECLARONS) ? 'Ex : Jardinage, travaux de bricolages...' : 'Ex : SVT, piano, anglais...'}
              callBack={(selectedSubjects: Subject[]) => {
                setCourses(selectedSubjects);
                const updatedForm = { ...form, courses: selectedSubjects };
                const error = validateField('courses', selectedSubjects, updatedForm);
                if (error) {
                  setErrors({ ...errors, courses: error });
                } else { clearError('courses') }
              }}
              error={errors.courses}
              required={true}
            />
          </div>
          {teachr?.sponsorship ? (
            ''
          ) : (
            <ReferralFields
              option={option}
              onOptionChange={getOption}
              recommandedBy={form.recommandedBy || ''}
              onRecommandedByChange={(value) => setField('recommandedBy', value)}
              sponsorship={form.sponsorship || ''}
              onSponsorshipChange={(value) => setField('sponsorship', value)}
              sponsorshipError={errors.sponsorship}
              codeRequired={isBrandActive(ENV_VERIFIED_TRAINER) || option === 2}
            />
          )}
          {!isBrandActive(ENV_VERIFIED_TRAINER) && <div className="w-full lg:col-span-2 flex flex-col gap-y-3">
            <p className={`text-sm font-semibold ${errors.homeCoursesOnly ? classes.error : 'text-gray-700'}`}>
              Facturez-vous <strong>uniquement</strong> des {getActivePrestationBrandConfig("s")} à domicile ? <span className="text-red-500">*</span>
            </p>
           
            <div className={`flex gap-3 rounded-md ${errors.homeCoursesOnly ? `border-l-4 ${classes.errorBorder} pl-2` : ''}`}>
              <ButtonField
                label={"Oui, uniquement à domicile"}
                variant={homeCoursesOnly === true ? 'contained' : 'outlined'}
                className={`font-bold w-full`}
                onClick={() => { setHomeCoursesOnly(true); clearError('homeCoursesOnly'); }}
              />
              <ButtonField
                label={"Non, d'autres prestations"}
                variant={homeCoursesOnly === false ? 'contained' : 'outlined'}
                className={`font-bold w-full`}
                onClick={() => { setHomeCoursesOnly(false); clearError('homeCoursesOnly'); }}
              />
            </div>
            {errors.homeCoursesOnly && (
              <p className={`text-xs mt-1 ${classes.error} font-medium`}>{errors.homeCoursesOnly}</p>
            )}
          </div>}
          {showStudentCount && (
            <div className="w-full lg:col-span-2 flex flex-col gap-y-4">
              <FormInput
                label={isBrandActive(ENV_DECLARONS) ? 'Combien de clients avez-vous ?' : "Combien d'élèves avez-vous ?"}
                name="numberOfStudents"
                type="number"
                placeholder="Ex : 5"
                value={studentCount}
                error={errors.numberOfStudents}
                required
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setStudentCount('');
                    setWantsAdvice(null);
                    clearError('numberOfStudents');
                    clearError('wantsAdvice');
                    return;
                  }
                  const parsed = Number(raw);
                  if (!Number.isNaN(parsed) && parsed >= 0) {
                    setStudentCount(parsed);
                    clearError('numberOfStudents');
                    if (parsed >= 3) {
                      setWantsAdvice(null);
                      clearError('wantsAdvice');
                    }
                  }
                }}
              />
              {studentCount !== '' && Number(studentCount) < 3 && (
                <div className="w-full flex flex-col gap-y-3">
                
                  <p className={`text-sm font-semibold ${errors.wantsAdvice ? classes.error : 'text-gray-700'}`}>
                    Souhaitez-vous recevoir des conseils pour développer votre activité ? <span className="text-red-500">*</span>
                  </p>
                  <div className={`flex gap-3 rounded-md ${errors.wantsAdvice ? `border-l-4 ${classes.errorBorder} pl-2` : ''}`}>
                    <ButtonField
                      label="Oui, ça m’intéresse"
                      variant={wantsAdvice === true ? 'contained' : 'outlined'}
                      className="font-bold w-full"
                      onClick={() => { setWantsAdvice(true); clearError('wantsAdvice'); }}
                    />
                    <ButtonField
                      label="Non"
                      variant={wantsAdvice === false ? 'contained' : 'outlined'}
                      className="font-bold w-full"
                      onClick={() => { setWantsAdvice(false); clearError('wantsAdvice'); }}
                    />
                  </div>
                  {errors.wantsAdvice && (
                    <p className={`text-xs mt-1 ${classes.error} font-medium`}>{errors.wantsAdvice}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      }
    />
  );
}

export default RegisterSecond;


